/**
 * selectionLogic
 *
 * Reine, framework-freie Entscheidungslogik der Score-Selektion (kein OSMD/Vue) – so unit-testbar,
 * ohne einen OSMD-Mock. Sub-Logik von {@link @/composables/osmd/useScoreSelection}.
 */
import type {GraphicalNote} from 'opensheetmusicdisplay';

import type {IndexEdit, NoteInfo} from '@/composables/musicXmlDom';

/**
 * Takte, deren **sämtliche** Staffs/Notenzeilen komplett in `selectedKeys` ausgewählt sind. Nur solche
 * Takte dürfen score-weit gelöscht werden (`deleteMeasuresInMusicXml`); ist ein Takt nur in *einem* von
 * mehreren Parts voll ausgewählt, gilt er hier **nicht** als voll → Löschen wandelt die Noten stattdessen
 * in Pausen um. Bei einteiligen Stücken (nur ein Staff) entspricht das dem einfachen „alle Noten des
 * Takts ausgewählt".
 *
 * @param selectedKeys    Menge der `noteKey`-Werte der aktuellen Auswahl.
 * @param measureStaffKeys `measure → (staff → alle noteKeys dieses (measure,staff))` (Soll-Bestand).
 */
export function computeFullMeasures(
    selectedKeys: Set<string>,
    measureStaffKeys: Map<number, Map<number, string[]>>,
): number[] {
    const out: number[] = [];
    for (const [measure, staffMap] of measureStaffKeys) {
        const staffs = [...staffMap.values()];
        if (!staffs.length) continue;
        if (staffs.every(keys => keys.length > 0 && keys.every(k => selectedKeys.has(k)))) out.push(measure);
    }
    return [...new Set(out)].sort((a, b) => a - b);
}

/**
 * Remap der Auswahl nach einem Edit, der Akkordtöne **eingefügt/entfernt** hat (`IndexEdit`s in
 * Anwendungsreihenfolge, aus `addDiatonicChordTonesInMusicXml`/`reduceTriadsInMusicXml`). Pro Edit
 * derselben Notenzeile+Takt+Stimme: Einfügen an Position `p` verschiebt alle Infos mit
 * `voiceNoteIndex ≥ p` um +1 (ein unterhalb eingefügter Ton verschiebt also auch die Quellnote);
 * Entfernen an `p` verwirft die Info mit `voiceNoteIndex === p` und verschiebt `> p` um −1.
 * **`noteIndex` (OSMDs flache, stimmen-verschränkte Zählung) wird für jede Info derselben
 * Notenzeile+Takt auf `-1` = veraltet gesetzt** – er lässt sich aus dem XML bei mehrstimmigen Staffs
 * nicht zuverlässig berechnen und wird nach dem Re-Render neu abgeleitet
 * (`useScoreSelection.restoreSelectionHighlight` → `hitTesting.findGraphicalNoteByVoiceIndex`).
 * Andere Takte/Notenzeilen bleiben unverändert.
 */
export function remapSelectionAfterIndexEdits(infos: readonly NoteInfo[], edits: readonly IndexEdit[]): NoteInfo[] {
    const measureIdentity = (i: { measureIndex?: number; measureNumber: number }) =>
        i.measureIndex === undefined ? `number:${i.measureNumber}` : `index:${i.measureIndex}`;
    let out: NoteInfo[] = infos.map(i => ({...i}));
    for (const e of edits) {
        const next: NoteInfo[] = [];
        for (const i of out) {
            const sameMeasure = i.staffIndex === e.staffIndex && measureIdentity(i) === measureIdentity(e);
            if (!sameMeasure) {
                next.push(i);
                continue;
            }
            const info = {...i, noteIndex: -1};
            if (i.voice === e.voice) {
                if (e.delta > 0 && i.voiceNoteIndex >= e.voiceNoteIndex) info.voiceNoteIndex += 1;
                else if (e.delta < 0) {
                    if (i.voiceNoteIndex === e.voiceNoteIndex) continue; // Ton wurde entfernt
                    if (i.voiceNoteIndex > e.voiceNoteIndex) info.voiceNoteIndex -= 1;
                }
            }
            next.push(info);
        }
        out = next;
    }
    return out;
}

/**
 * Leitet einen Treffer auf eine **versteckte** Note/Pause (`print-object="no"`, `sourceNote.PrintObject
 * === false`) auf die erste **druckbare** Note derselben Zählzeit (gleicher `parentStaffEntry`) um.
 * Hintergrund: die Bass-Träger-Stimme der Griffschrift-Ausgabe füllt Lücken mit versteckten Pausen;
 * die Trefferprüfung wählt den nächstgelegenen Notenkopf, sodass am Taktanfang die unsichtbare
 * Füll-Pause (Staff-Mitte) eine hohe/tiefe Melodie-Note „überstimmen" kann – der Klick auf eine
 * Bass-Silbe träfe dann eine Note ohne Lyric. Druckbare Treffer bleiben unverändert;
 * ohne druckbare Alternative bleibt die versteckte Note (sie ist trotzdem adressierbar).
 */
export function preferPrintableNote(note: GraphicalNote | null): GraphicalNote | null {
    if (!note || note.sourceNote?.PrintObject !== false) return note;
    const entries = note.parentVoiceEntry?.parentStaffEntry?.graphicalVoiceEntries ?? [];
    for (const entry of entries) {
        // `find`, NICHT `filter(...).length === 1` (kritisch – nicht rückbauen): ein AKKORD hat
        // mehrere druckbare Töne, und die Längenprüfung übersprang ihn – die Umleitung blieb genau
        // dort wirkungslos, wo sie gebraucht wird (Oktav-Akkorde der Melodie).
        const printable = (entry.notes ?? []).find((n) => n.sourceNote?.PrintObject !== false);
        if (printable) return printable;
    }
    return note;
}

/**
 * Ein Rechteck in Client-Koordinaten (`getBoundingClientRect()`-Ausschnitt) – framework-frei, damit
 * {@link resolvePageAt} ohne DOM-Mock testbar bleibt.
 */
export interface PageRect {
    readonly left: number;
    readonly top: number;
    readonly right: number;
    readonly bottom: number;
}

/**
 * Index der Seite, auf die ein Client-Punkt fällt (`-1`, wenn es keine Seiten gibt).
 *
 * OSMD stapelt bei `pageFormat: 'A4_P'` ein `<svg>` je Seite im Container, und alle Seiten teilen
 * sich **denselben** Koordinatenursprung (siehe `osmd/pageSvgs.ts`). Ohne diese Zuordnung landet
 * jede Umrechnung ab Seite 2 im Koordinatenraum von Seite 1.
 *
 * Liegt der Punkt in keinem Rechteck (Klick in den Zwischenraum zweier Seiten), gewinnt die
 * vertikal nächstgelegene Seite – Seiten stapeln sich untereinander, der vertikale Abstand wiegt
 * daher schwerer als der horizontale. Bei Gleichstand gewinnt die frühere Seite.
 */
export function resolvePageAt(rects: readonly PageRect[], clientX: number, clientY: number): number {
    if (!rects.length) return -1;
    for (let i = 0; i < rects.length; i++) {
        const r = rects[i]!;
        if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) return i;
    }
    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < rects.length; i++) {
        const r = rects[i]!;
        const dy = clientY < r.top ? r.top - clientY : clientY > r.bottom ? clientY - r.bottom : 0;
        const dx = clientX < r.left ? r.left - clientX : clientX > r.right ? clientX - r.right : 0;
        const distance = dy * 1000 + dx;
        if (distance < bestDistance) {
            bestDistance = distance;
            best = i;
        }
    }
    return best;
}

/** Normalisiertes Rechteck aus zwei beliebigen Eckpunkten – die Eckreihenfolge ist egal
 *  (ein Rubber-Band kann in jede Richtung aufgezogen werden). */
export function normalizedRect(x1: number, y1: number, x2: number, y2: number): PageRect {
    return {
        left: Math.min(x1, x2),
        top: Math.min(y1, y2),
        right: Math.max(x1, x2),
        bottom: Math.max(y1, y2),
    };
}

/** Schnittmenge zweier Rechtecke; `null` bei leerem Schnitt (Kantenberührung zählt als Schnitt
 *  der Breite/Höhe 0 – ein Punkt exakt auf der Kante gilt überall sonst auch als „drin"). */
export function intersectRects(a: PageRect, b: PageRect): PageRect | null {
    const left = Math.max(a.left, b.left);
    const top = Math.max(a.top, b.top);
    const right = Math.min(a.right, b.right);
    const bottom = Math.min(a.bottom, b.bottom);
    if (left > right || top > bottom) return null;
    return {left, top, right, bottom};
}

/**
 * Kandidaten, deren **Notenkopf-Mittelpunkt** im Rechteck liegt (Ränder inklusiv).
 *
 * Mittelpunkt-Semantik statt „Berührung genügt" (kritisch – nicht auf BBox-Schnitt rückbauen):
 * dieselbe Koordinatenbasis wie {@link nearestNoteAmong} (Klick/Hover), damit ein Rechteck exakt
 * die Noten selektiert, die ein Klick an derselben Stelle träfe – und ein knapp vorbeigezogenes
 * Rechteck keine halb berührten Nachbarnoten einsammelt.
 */
export function candidatesInRect<TNote>(
    candidates: Iterable<NoteCandidate<TNote>>,
    rect: PageRect,
): TNote[] {
    const out: TNote[] = [];
    for (const c of candidates) {
        if (c.x >= rect.left && c.x <= rect.right && c.y >= rect.top && c.y <= rect.bottom) out.push(c.note);
    }
    return out;
}

/** Hat die Zeigerbewegung den Drag-Schwellwert überschritten (Chebyshev-Distanz, Client-Pixel)?
 *  Unterhalb bleibt die Geste ein normaler Klick – auch der Doppelklick-Pfad bleibt so unberührt. */
export function exceedsDragThreshold(
    startX: number, startY: number, x: number, y: number, threshold: number,
): boolean {
    return Math.max(Math.abs(x - startX), Math.abs(y - startY)) > threshold;
}

/** Ein Trefferkandidat: die Note plus der **Mittelpunkt ihres Notenkopfs** in Sheet-Einheiten. */
export interface NoteCandidate<TNote> {
    readonly note: TNote;
    readonly x: number;
    readonly y: number;
}

/**
 * Die dem Punkt nächste Note – oder `null`, wenn keine innerhalb von `maxDistance` liegt.
 *
 * **Gemessen wird zum Notenkopf, nicht zum Anker des VoiceEntry (kritisch – nicht rückbauen).**
 * OSMDs `GetNearestVoiceEntry` vergleicht den Abstand zum BBox-**Ursprung** des VoiceEntry und
 * weitet seinen Suchradius (5 → 20 Einheiten), bis irgendetwas gefunden ist. Bei einem
 * Oktav-Akkord oder einer Note weit über dem System (Hilfslinien) liegt der Klick mehrere
 * Einheiten vom Anker entfernt – der Radius wuchs, und in der größeren Box gewannen Noten anderer
 * Systeme/Seiten. Hier gilt stattdessen eine **feste** Schranke: kein Treffer ⇒ `null`, und das
 * bedeutet stromabwärts „freier Bereich" (Ganztakt-Auswahl).
 *
 * Distanzmaß ist `|dx| + |dy|` (wie OSMDs Notenauswahl innerhalb eines VoiceEntry); bei
 * Gleichstand gewinnt der erste Kandidat in Traversierungsreihenfolge (strikt `<`).
 */
export function nearestNoteAmong<TNote>(
    candidates: Iterable<NoteCandidate<TNote>>,
    pos: {x: number; y: number},
    maxDistance: number,
): TNote | null {
    let best: TNote | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const candidate of candidates) {
        const distance = Math.abs(candidate.x - pos.x) + Math.abs(candidate.y - pos.y);
        if (distance < bestDistance) {
            bestDistance = distance;
            best = candidate.note;
        }
    }
    return best !== null && bestDistance <= maxDistance ? best : null;
}
